import { useDisclosure } from "@mantine/hooks";
import { Box, Group, Stack, Text } from "@mantine/core";
import { IconAlertOctagon, IconListSearch, IconPlus, IconTrash } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { InputDropdown } from "@onengine/component-library";
import {
  useExclusionOptionsByTypes,
  useExclusionTypes,
} from "@/hooks/user-management/useUserManagementQueries";
import CollapsableCard from "@/components/ui/CollapsableCard/CollapsableCard";
import { Button } from "@/components/ui/Button/Button";
import { Select } from "@/components/ui/Select/Select";
import type { DropDownOption } from "@/types/common.types";
import type { FormValues } from "@/routes/user-management/add-user";
import { EntityRuleDrawer, type EntityRuleDrawerKind } from "./EntityRuleDrawer";
import styles from "./add-user.module.scss";

type EntityRule = { type: string; valueIds: string[] };
type EntityRulesEntry = { inclusions: EntityRule[]; exclusions: EntityRule[] };
type EntityRulesRecord = Record<string, EntityRulesEntry>;

const INCLUDE_EXCLUDE_BY_EMPTY_OPTION: DropDownOption = { value: "", label: "" };

const CONFLICT_INCLUSION_MSG = "Conflicts with inclusion. Delete before adding as exclusion.";
const CONFLICT_EXCLUSION_MSG = "Conflicts with exclusion. Delete before adding as inclusion.";

export interface EntityAccessStepSectionProps {
  entityAccessOptions: DropDownOption[];
  isLoading?: boolean;
  hasMoreEntityAccess?: boolean;
  isLoadingMoreEntityAccess?: boolean;
  onLoadMoreEntityAccess?: () => void;
  /**
   * Set when the session can only ever grant one entity, which is every
   * entity-scoped session: the backend's entity dropdown returns just that
   * tenant (`userManagement/repository.ts`, `case "entity"`). The picker is then
   * a mandatory single-option control that poses a choice it cannot offer, so it
   * is not rendered and the inclusion/exclusion cards stand alone against this
   * entity (SERV-2707).
   *
   * Null means the server offered more than one entity, and the picker is real.
   */
  scopedEntityId?: string | null;
}

type EntityRuleDrawerContext = {
  entityValue: string;
  ruleIndex: number;
  kind: EntityRuleDrawerKind;
  ruleType: string;
  ruleTypeLabel: string;
  options: DropDownOption[];
  selectedValueIds: string[];
};

export function EntityAccessStepSection({
  entityAccessOptions,
  isLoading = false,
  hasMoreEntityAccess = false,
  isLoadingMoreEntityAccess = false,
  onLoadMoreEntityAccess,
  scopedEntityId = null,
}: EntityAccessStepSectionProps) {
  const {
    watch,
    setValue,
    control,
    formState: { errors, submitCount },
  } = useFormContext<FormValues>();

  const [drawerOpened, drawerHandlers] = useDisclosure(false);
  const [drawerContext, setDrawerContext] = useState<EntityRuleDrawerContext | null>(null);

  const watchedEntityAccess = watch("entityAccess");
  const watchedEntityRules = watch("entityRules") ?? {};

  const entityRulesMap = (watchedEntityRules ?? {}) as EntityRulesRecord;

  const { data: exclusionTypesData, isLoading: isExclusionTypesLoading } = useExclusionTypes();
  // Use category id as option value so payload categoryId is the id, not the display value
  const exclusionTypeOptions: DropDownOption[] = useMemo(() => {
    const items = exclusionTypesData?.data ?? [];
    return items.map((item) => ({
      label: item.label,
      value: item.id != null ? String(item.id) : item.value,
    }));
  }, [exclusionTypesData?.data]);

  // rule.type stores category id (for Create User payload); dropdown API needs value
  const allRuleTypeIdsInUse = useMemo(() => {
    const ids = new Set<string>();
    for (const entry of Object.values(entityRulesMap)) {
      for (const r of entry.inclusions) if (r.type) ids.add(r.type);
      for (const r of entry.exclusions) if (r.type) ids.add(r.type);
    }
    return Array.from(ids);
  }, [entityRulesMap]);

  const idToValueMap = useMemo(() => {
    const items = exclusionTypesData?.data ?? [];
    return Object.fromEntries(
      items.filter((i) => i.id != null).map((i) => [String(i.id), i.value]),
    );
  }, [exclusionTypesData?.data]);

  const { optionsByRuleType, isLoadingByRuleType } = useExclusionOptionsByTypes(
    allRuleTypeIdsInUse,
    idToValueMap,
  );

  const getRuleTypeConfig = (
    ruleType: string,
  ): { label: string; options: DropDownOption[]; isLoading?: boolean } | null => {
    const typeOption = exclusionTypeOptions.find((o) => o.value === ruleType);
    const options = optionsByRuleType[ruleType];
    const isOptionsLoading = isLoadingByRuleType[ruleType] === true;
    if (!typeOption) return null;
    return {
      label: `Select ${typeOption.label}`,
      options: options ?? [],
      isLoading: isOptionsLoading,
    };
  };

  useEffect(() => {
    const entities = (watchedEntityAccess ?? []) as string[];
    const rules: EntityRulesRecord = {
      ...((watchedEntityRules ?? {}) as EntityRulesRecord),
    };
    let updated = false;

    for (const entityValue of entities) {
      if (!rules[entityValue]) {
        rules[entityValue] = { inclusions: [], exclusions: [] };
        updated = true;
      }
    }

    for (const key of Object.keys(rules)) {
      if (!entities.includes(key)) {
        delete rules[key];
        updated = true;
      }
    }

    if (updated) setValue("entityRules", rules, { shouldValidate: false });
  }, [watchedEntityAccess, setValue, watchedEntityRules]);

  const getEntityRules = (entityValue: string): EntityRulesEntry => {
    const r = entityRulesMap[entityValue];
    return r ?? { inclusions: [], exclusions: [] };
  };

  const getRuleValueError = (
    entityValue: string,
    kind: EntityRuleDrawerKind,
    index: number,
  ): string | undefined => {
    if (submitCount === 0) {
      return undefined;
    }

    const rulesKey = kind === "inclusion" ? "inclusions" : "exclusions";
    const message = (
      errors.entityRules as
        | Record<
            string,
            {
              inclusions?: Array<{ valueIds?: { message?: string } }>;
              exclusions?: Array<{ valueIds?: { message?: string } }>;
            }
          >
        | undefined
    )?.[entityValue]?.[rulesKey]?.[index]?.valueIds?.message;

    if (typeof message === "string" && message.trim().length > 0) {
      return message;
    }

    return undefined;
  };

  const addInclusion = (entityValue: string) => {
    const rules = { ...getEntityRules(entityValue) };
    rules.inclusions = [...rules.inclusions, { type: "", valueIds: [] }];
    setValue(
      "entityRules",
      { ...entityRulesMap, [entityValue]: rules },
      {
        shouldValidate: false,
      },
    );
  };

  const addExclusion = (entityValue: string) => {
    const rules = { ...getEntityRules(entityValue) };
    rules.exclusions = [...rules.exclusions, { type: "", valueIds: [] }];
    setValue(
      "entityRules",
      { ...entityRulesMap, [entityValue]: rules },
      {
        shouldValidate: false,
      },
    );
  };

  const removeInclusion = (entityValue: string, index: number) => {
    const rules = { ...getEntityRules(entityValue) };
    rules.inclusions = rules.inclusions.filter((_, i) => i !== index);
    setValue(
      "entityRules",
      { ...entityRulesMap, [entityValue]: rules },
      {
        shouldValidate: false,
      },
    );
  };

  const removeExclusion = (entityValue: string, index: number) => {
    const rules = { ...getEntityRules(entityValue) };
    rules.exclusions = rules.exclusions.filter((_, i) => i !== index);
    setValue(
      "entityRules",
      { ...entityRulesMap, [entityValue]: rules },
      {
        shouldValidate: false,
      },
    );
  };

  const updateInclusionRule = (entityValue: string, index: number, patch: Partial<EntityRule>) => {
    const rules = { ...getEntityRules(entityValue) };
    const list = [...rules.inclusions];
    list[index] = { ...list[index], ...patch };
    rules.inclusions = list;
    setValue(
      "entityRules",
      { ...entityRulesMap, [entityValue]: rules },
      {
        shouldValidate: false,
      },
    );
  };

  const updateExclusionRule = (entityValue: string, index: number, patch: Partial<EntityRule>) => {
    const rules = { ...getEntityRules(entityValue) };
    const list = [...rules.exclusions];
    list[index] = { ...list[index], ...patch };
    rules.exclusions = list;
    setValue(
      "entityRules",
      { ...entityRulesMap, [entityValue]: rules },
      {
        shouldValidate: false,
      },
    );
  };

  const openRuleDrawer = (
    entityValue: string,
    ruleIndex: number,
    kind: EntityRuleDrawerKind,
    ruleType: string,
    selectedValueIds: string[],
  ) => {
    const config = getRuleTypeConfig(ruleType);
    if (!config) return;
    const ruleTypeLabel = exclusionTypeOptions.find((o) => o.value === ruleType)?.label ?? ruleType;
    setDrawerContext({
      entityValue,
      ruleIndex,
      kind,
      ruleType,
      ruleTypeLabel,
      options: config.options,
      selectedValueIds,
    });
    drawerHandlers.open();
  };

  useEffect(() => {
    if (!drawerOpened && drawerContext) {
      const id = setTimeout(() => setDrawerContext(null), 300);
      return () => clearTimeout(id);
    }
  }, [drawerOpened, drawerContext]);

  return (
    <>
      {!scopedEntityId && (
        <Box className={styles.section}>
          <Group className={styles.sectionHeader} justify="space-between" align="center">
            <Text className={styles.sectionTitle}>Entity Access</Text>
          </Group>
          <Stack className={styles.sectionContent}>
            <Text className={styles.sectionDescription}>
              Choose which Entities the user will have access to.
            </Text>
            <Controller
              name="entityAccess"
              control={control}
              render={({ field }) => (
                <InputDropdown
                  mode="multi"
                  label="Entity Access"
                  placeholder="Select.."
                  items={entityAccessOptions}
                  error={errors.entityAccess?.message}
                  loading={isLoading || isLoadingMoreEntityAccess}
                  hasMore={hasMoreEntityAccess}
                  onLoadMore={() => onLoadMoreEntityAccess?.()}
                  searchable
                  value={field.value ?? []}
                  onChange={field.onChange}
                />
              )}
            />
          </Stack>
        </Box>
      )}

      <Stack className={styles.entityCardsList}>
        {(watchedEntityAccess ?? []).map((entityValue: string) => {
          const option = entityAccessOptions.find((o) => o.value === entityValue);
          const label = option?.label ?? entityValue;
          const rules = getEntityRules(entityValue);

          return (
            <CollapsableCard
              key={entityValue}
              title={label}
              fieldKeys={[`entityRules.${entityValue}`]}
              defaultState={true}
              border
              mb="0"
            >
              <Box className={styles.entityCardContent}>
                <Stack gap="xs">
                  <Text className={styles.entityCardDescription}>
                    Inclusions & Exclusions are mutually exclusive.
                  </Text>
                  <Text className={styles.entityCardSubDescription}>
                    Example: If 1 state is included, the system will automatically exclude all other
                    49.
                  </Text>
                </Stack>

                <Box className={styles.includeExcludeSection}>
                  {rules.inclusions.length > 0 && (
                    <>
                      {rules.inclusions.map((rule: EntityRule, idx: number) => {
                        const config = getRuleTypeConfig(rule.type);
                        const showValueField = Boolean(rule.type && config);
                        const usedInclusionTypes = rules.inclusions
                          .filter((_, i) => i !== idx)
                          .map((r) => r.type)
                          .filter(Boolean);
                        const includeByOptions: DropDownOption[] = [
                          INCLUDE_EXCLUDE_BY_EMPTY_OPTION,
                          ...exclusionTypeOptions.filter(
                            (opt) => !usedInclusionTypes.includes(opt.value),
                          ),
                        ];
                        const conflictsWithExclusion =
                          Boolean(rule.type) && rules.exclusions.some((e) => e.type === rule.type);
                        return (
                          <Box
                            key={`inc-${entityValue}-${idx}`}
                            className={styles.includeExcludeRuleRowWrapper}
                          >
                            <Box className={styles.includeExcludeRuleRow}>
                              <Select
                                label="Include By"
                                name={`entityRules.${entityValue}.inclusions.${idx}.type`}
                                options={includeByOptions}
                                control={control}
                                placeholder="Select.."
                                size="small"
                                searchable
                                loading={isExclusionTypesLoading}
                                error={conflictsWithExclusion ? true : undefined}
                                onChangeCallback={(value) => {
                                  updateInclusionRule(entityValue, idx, {
                                    type: value ?? "",
                                    valueIds: [],
                                  });
                                }}
                              />
                              <Group
                                className={styles.includeExcludeRuleValueCell}
                                align="flex-start"
                              >
                                {showValueField && config && (
                                  <Box className={styles.includeExcludeRuleValueCellMultiSelect}>
                                    <Controller
                                      name={`entityRules.${entityValue}.inclusions.${idx}.valueIds`}
                                      control={control}
                                      render={({ field }) => (
                                        <InputDropdown
                                          mode="multi"
                                          label={config.label}
                                          items={config.options}
                                          placeholder="Select.."
                                          loading={config.isLoading}
                                          error={getRuleValueError(entityValue, "inclusion", idx)}
                                          searchable
                                          value={field.value ?? []}
                                          onChange={field.onChange}
                                        />
                                      )}
                                    />
                                  </Box>
                                )}
                                <Group className={styles.includeExcludeRuleActions} align="center">
                                  {showValueField && (
                                    <Button
                                      type="button"
                                      className={styles.previewButton}
                                      onClick={() =>
                                        openRuleDrawer(
                                          entityValue,
                                          idx,
                                          "inclusion",
                                          rule.type,
                                          rule.valueIds,
                                        )
                                      }
                                      aria-label="Search or filter"
                                      disabled={config?.isLoading}
                                    >
                                      <IconListSearch size={16} title="Preview" />
                                    </Button>
                                  )}
                                  <Button
                                    type="button"
                                    className={styles.removeTemporaryRoleButton}
                                    onClick={() => removeInclusion(entityValue, idx)}
                                    aria-label="Remove inclusion rule"
                                  >
                                    <IconTrash
                                      size={16}
                                      color="var(--icon-danger-default)"
                                      title="Delete"
                                    />
                                  </Button>
                                </Group>
                              </Group>
                            </Box>
                            {conflictsWithExclusion && (
                              <Group
                                className={styles.includeExcludeRuleRowError}
                                align="center"
                                role="alert"
                              >
                                <IconAlertOctagon
                                  size={16}
                                  className={styles.includeExcludeRuleRowErrorIcon}
                                />
                                <Text className={styles.conflictErrMsg}>
                                  {CONFLICT_EXCLUSION_MSG}
                                </Text>
                              </Group>
                            )}
                          </Box>
                        );
                      })}
                    </>
                  )}
                  <Group gap="xs" justify="flex-start">
                    <Button
                      type="button"
                      className={styles.addInclusionButton}
                      iconLeft={<IconPlus size={16} />}
                      onClick={() => addInclusion(entityValue)}
                    >
                      Add Inclusion
                    </Button>
                  </Group>
                </Box>

                <Box className={styles.includeExcludeSection}>
                  {rules.exclusions.length > 0 && (
                    <>
                      {rules.exclusions.map((rule: EntityRule, idx: number) => {
                        const config = getRuleTypeConfig(rule.type);
                        const showValueField = Boolean(rule.type && config);
                        const usedExclusionTypes = rules.exclusions
                          .filter((_, i) => i !== idx)
                          .map((r) => r.type)
                          .filter(Boolean);
                        const excludeByOptions: DropDownOption[] = [
                          INCLUDE_EXCLUDE_BY_EMPTY_OPTION,
                          ...exclusionTypeOptions.filter(
                            (opt) => !usedExclusionTypes.includes(opt.value),
                          ),
                        ];
                        const conflictsWithInclusion =
                          Boolean(rule.type) && rules.inclusions.some((i) => i.type === rule.type);
                        return (
                          <Box
                            key={`exc-${entityValue}-${idx}`}
                            className={styles.includeExcludeRuleRowWrapper}
                          >
                            <Box className={styles.includeExcludeRuleRow}>
                              <Select
                                label="Exclude By"
                                name={`entityRules.${entityValue}.exclusions.${idx}.type`}
                                options={excludeByOptions}
                                control={control}
                                placeholder="Select.."
                                size="small"
                                loading={isExclusionTypesLoading}
                                error={conflictsWithInclusion ? true : undefined}
                                onChangeCallback={(value) => {
                                  updateExclusionRule(entityValue, idx, {
                                    type: value ?? "",
                                    valueIds: [],
                                  });
                                }}
                              />
                              <Group
                                className={styles.includeExcludeRuleValueCell}
                                align="flex-start"
                              >
                                {showValueField && config && (
                                  <Box className={styles.includeExcludeRuleValueCellMultiSelect}>
                                    <Controller
                                      name={`entityRules.${entityValue}.exclusions.${idx}.valueIds`}
                                      control={control}
                                      render={({ field }) => (
                                        <InputDropdown
                                          mode="multi"
                                          label={config.label}
                                          items={config.options}
                                          placeholder="Select.."
                                          loading={config.isLoading}
                                          error={getRuleValueError(entityValue, "exclusion", idx)}
                                          searchable
                                          value={field.value ?? []}
                                          onChange={field.onChange}
                                        />
                                      )}
                                    />
                                  </Box>
                                )}
                                <Group className={styles.includeExcludeRuleActions} align="center">
                                  {showValueField && (
                                    <Button
                                      type="button"
                                      className={styles.previewButton}
                                      onClick={() =>
                                        openRuleDrawer(
                                          entityValue,
                                          idx,
                                          "exclusion",
                                          rule.type,
                                          rule.valueIds,
                                        )
                                      }
                                      aria-label="Search or filter"
                                      disabled={config?.isLoading}
                                    >
                                      <IconListSearch size={16} title="Preview" />
                                    </Button>
                                  )}
                                  <Button
                                    type="button"
                                    className={styles.removeTemporaryRoleButton}
                                    onClick={() => removeExclusion(entityValue, idx)}
                                    aria-label="Remove exclusion rule"
                                  >
                                    <IconTrash
                                      size={16}
                                      color="var(--icon-danger-default)"
                                      title="Delete"
                                    />
                                  </Button>
                                </Group>
                              </Group>
                            </Box>
                            {conflictsWithInclusion && (
                              <Group
                                className={styles.includeExcludeRuleRowError}
                                align="center"
                                role="alert"
                              >
                                <IconAlertOctagon
                                  size={16}
                                  className={styles.includeExcludeRuleRowErrorIcon}
                                />
                                <Text className={styles.conflictErrMsg}>
                                  {CONFLICT_INCLUSION_MSG}
                                </Text>
                              </Group>
                            )}
                          </Box>
                        );
                      })}
                    </>
                  )}
                  <Group gap="xs" justify="flex-start">
                    <Button
                      type="button"
                      className={styles.addInclusionButton}
                      iconLeft={<IconPlus size={16} />}
                      onClick={() => addExclusion(entityValue)}
                    >
                      Add Exclusion
                    </Button>
                  </Group>
                </Box>
              </Box>
            </CollapsableCard>
          );
        })}
      </Stack>

      {drawerContext && (
        <EntityRuleDrawer
          opened={drawerOpened}
          onClose={() => {
            drawerHandlers.close();
            setDrawerContext(null);
          }}
          kind={drawerContext.kind}
          ruleTypeLabel={drawerContext.ruleTypeLabel}
          options={drawerContext.options}
          selectedValueIds={drawerContext.selectedValueIds}
        />
      )}
    </>
  );
}
